import { useState } from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@stockline/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@stockline/ui/components/dialog";
import { downloadMedia } from "./useChatMediaUrl";

/**
 * WhatsApp-style inline image: just the picture (no name/size/type chrome),
 * tap to open a full-screen black viewer with a single download action.
 */
export function ChatMediaImage({
  url,
  name,
}: {
  readonly url: string | null;
  readonly name: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!url) {
    return <div className="h-40 w-full animate-pulse rounded-2xl bg-muted" />;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block w-full overflow-hidden rounded-2xl"
        data-testid="chat-media-image"
      >
        <img
          src={url}
          alt={name}
          loading="lazy"
          className="max-h-[280px] w-full object-cover transition-[filter] hover:brightness-95"
        />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="h-[100dvh] max-h-none w-screen max-w-none border-none bg-black/95 p-0 text-white sm:rounded-none">
          <DialogTitle className="sr-only">{name}</DialogTitle>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => void downloadMedia(url, name)}
            aria-label={t("chat.download", "Télécharger")}
            className="absolute right-14 top-3 z-10 border-white/20 bg-black/50 text-white hover:bg-black/70"
          >
            <Download className="size-4" />
          </Button>
          <div className="flex h-full w-full items-center justify-center p-3">
            <img
              src={url}
              alt={name}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
